import { useEffect, useRef, useCallback } from 'react';

interface UseAutoaddressOptions {
  licenceKey: string;
  addressProfile?: string;
  vanityMode?: boolean;
  country?: string;
  onAddressSelected?: (address: any) => void;
  placeHolderLabel?: string;
}

export const useAutoaddress = (
  inputSelector: string,
  setUserDetails: React.Dispatch<React.SetStateAction<Record<string, string> | null>>,
  setIsManualEntry: React.Dispatch<React.SetStateAction<boolean>>,
  isManualEntry: boolean,
  options: UseAutoaddressOptions
) => {
  const initializationRef = useRef(false);

  const handleAddressFound = useCallback(
    (args: any) => {
      const removeErrorStyling = (fieldIds: string[]) => {
        fieldIds.forEach(fieldId => {
          document.getElementById(fieldId)?.classList.remove('portal-invoice-payment__input--error');
        });
      };

      const updates: Record<string, string> = {};
      const updatedFields: string[] = [];

      if (args.reformattedAddress) {
        const addressFields = ['addressLine1', 'addressLine2', 'addressLine3', 'townCity'];
        addressFields.forEach((field, index) => {
          updates[field] = args.reformattedAddress[index] || '';
        });
        updatedFields.push(...addressFields);
      }

      if (args.postcode) {
        updates.postCode = args.postcode || '';
        updatedFields.push('postCode');
      }

      removeErrorStyling(updatedFields);
      setUserDetails(prev => ({ ...prev, ...updates }));
      setIsManualEntry(true);
    },
    [setUserDetails, setIsManualEntry]
  );

  const initializeAutoaddress = useCallback(async () => {
    const $ = (globalThis as any).jQuery;
    if (!$) {
      console.error('jQuery not available');
      return;
    }

    const $input = $(inputSelector);
    if ($input.length === 0) {
      console.error(`Input element not found: ${inputSelector}`);
      return;
    }

    try {
      $(function () {
        $input.AutoAddress({
          key: options.licenceKey,
          vanityMode: options.vanityMode ?? true,
          country: options.country ?? 'ie',
          addressProfile: options.addressProfile,
          addressElements: true,
          placeHolderLabel: options.placeHolderLabel ?? 'Type to search address',
          onAddressFound: handleAddressFound,
        });
        initializationRef.current = true;
      });
    } catch (error) {
      console.error('Failed to initialize Autoaddress:', error);
    }
    // WARNING: `options` object should be memoized by caller to prevent unnecessary re-initialization
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputSelector, handleAddressFound]);

  useEffect(() => {
    // Reset initialization flag when switching from manual to search mode
    if (!isManualEntry) {
      initializationRef.current = false;
    }
  }, [isManualEntry]);

  useEffect(() => {
    // Prevent re-initialization if already done
    if (initializationRef.current || isManualEntry) {
      return;
    }

    initializeAutoaddress();
    // WARNING: `options` and `setUserDetails`/`setIsManualEntry` are intentionally omitted
    // to prevent re-initialization when these change. Caller should memoize `options`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManualEntry, initializeAutoaddress]);
};
